import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, BookOpen, Heart, Trash2, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import GuestModeBanner from '@/components/GuestModeBanner';
import { getLevel } from '@/utils/readingLevels';

const StoryLibrary = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, nightMode } = useContext(AuthContext);
  const isGuestMode = user?.id === 'guest-user';
  const isAdmin = user?.role === 'admin';
  const userTier = user?.subscription_tier || 'free';  
  const [stories, setStories] = useState([]);
  const [avatars, setAvatars] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const lastFetched = useRef(0);

  // Filter states
  const [selectedChildren, setSelectedChildren] = useState(
    searchParams.get('child') ? [searchParams.get('child')] : []
  );
  const [selectedAvatars, setSelectedAvatars] = useState(
    searchParams.get('avatar') ? [searchParams.get('avatar')] : []
  );
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedAges, setSelectedAges] = useState([]);
  const [filterFavorite, setFilterFavorite] = useState(false);
  const [filterFolio, setFilterFolio] = useState(
    searchParams.get('is_folio') === 'true'
  );

  // Sequential faceted filtering: reduces options based on "earlier" filters (top-to-bottom in UI)
  // 1. Base set filtered by Global Flags (Fav/Folio)
  const storiesByFlags = stories.filter(s => {
    if (filterFavorite && !s.is_favorite) return false;
    if (filterFolio && !s.is_folio_story) return false;
    return true;
  });

  // Calculate available children based on flags
  const childrenInStories = new Set(storiesByFlags.map(s => s.child_id));
  const availableChildrenOptions = children.filter(c => childrenInStories.has(c.id) || selectedChildren.includes(c.id));

  // 2. Filter by phase 1 + Selected Children
  const storiesWithChildren = storiesByFlags.filter(s => 
    selectedChildren.length === 0 || selectedChildren.includes(s.child_id)
  );

  // Calculate available avatars based on current phase
  const avatarsInStories = new Set(storiesWithChildren.flatMap(s => s.characters || []));
  const availableAvatarsOptions = avatars.filter(a => avatarsInStories.has(a.name) || selectedAvatars.includes(a.name));

  // 3. Filter by phase 2 + Selected Avatars
  const storiesWithAvatars = storiesWithChildren.filter(s => {
    const storyChars = s.characters || [];
    return selectedAvatars.length === 0 || selectedAvatars.some(name => storyChars.includes(name));
  });

  // Calculate available topics based on phase 3
  const availableTopics = [...new Set(storiesWithAvatars.map(s => s.topic).filter(Boolean))].sort();

  // 4. Filter by phase 3 + Selected Topics
  const storiesWithTopics = storiesWithAvatars.filter(s => 
    selectedTopics.length === 0 || selectedTopics.some(topic => s.topic?.toLowerCase().includes(topic.toLowerCase()))
  );

  // Calculate available ages based on phase 4
  const availableAges = [...new Set(storiesWithTopics.map(s => getLevel(s.reading_level)).filter(Boolean))].sort();

  // Final filtered list for display (Phase 5: Age)
  const filteredStories = storiesWithTopics.filter(s => 
    selectedAges.length === 0 || selectedAges.includes(getLevel(s.reading_level))
  );

  const fetchChildren = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/children/list/names`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChildren(response.data);
    } catch (error) {
      console.error('Failed to fetch children:', error);
    }
  }, [token]);

  const fetchAvatars = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/avatars/list/names`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvatars(response.data);
    } catch (error) {
      console.error('Failed to fetch avatars:', error);
    }
  }, [token]);

  const fetchStories = useCallback(async (append = false) => {
    if (!append) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const params = {
        limit: 24
      };
      
      if (append) {
        params.skip = stories.length;
      }
      
      const response = await axios.get(`${API}/stories/metadata`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      if (append) {
        setStories(prev => [...prev, ...response.data]);
      } else {
        setStories(response.data);
      }
      
      setHasMore(response.data.length === 24);
    } catch (error) {
      console.error('Failed to fetch stories:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load stories');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [token, stories.length]);

  useEffect(() => {
    const fetchData = () => {
      fetchChildren();
      fetchAvatars();
      fetchStories();
      lastFetched.current = Date.now();
    };

    // Initial fetch
    if (lastFetched.current === 0) {
      fetchData();
    }

    const handleFocus = () => {
      // Only reload if more than 5 minutes have passed since last fetch
      if (Date.now() - lastFetched.current > 5 * 60 * 1000) {
        fetchData();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchChildren, fetchAvatars, fetchStories]);

  const handleToggleFavorite = async (e, storyId, currentStatus) => {
    e.stopPropagation();
    try {
      await axios.patch(
        `${API}/stories/${storyId}`,
        { is_favorite: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(!currentStatus ? 'Added to favorites' : 'Removed from favorites');
      setStories(prev => prev.map(s => 
        s.id === storyId ? { ...s, is_favorite: !currentStatus } : s
      ));
    } catch (error) {
      toast.error('Failed to update favorite status');
    }
  };

  const handleDelete = async (e, storyId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this story?')) return;
    
    try {
      await axios.delete(`${API}/stories/${storyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Story deleted');
      setStories(prev => prev.filter(s => s.id !== storyId));
    } catch (error) {
      toast.error('Failed to delete story');
    }
  };

  // Toggle functions for label filters
  const toggleChild = (childId) => {
    setSelectedChildren(prev => 
      prev.includes(childId) ? prev.filter(c => c !== childId) : [...prev, childId]
    );
  };

  const toggleAvatar = (avatarName) => {
    setSelectedAvatars(prev => 
      prev.includes(avatarName) ? prev.filter(a => a !== avatarName) : [...prev, avatarName]
    );
  };

  const toggleTopic = (topic) => {
    setSelectedTopics(prev => 
      prev.includes(topic) ? prev.filter(t => t !== topic) : [...prev, topic]
    );
  };

  const toggleAge = (age) => {
    setSelectedAges(prev => 
      prev.includes(age) ? prev.filter(a => a !== age) : [...prev, age]
    );
  };

  const clearAllFilters = () => {
    setSelectedChildren([]);
    setSelectedAvatars([]);
    setSelectedTopics([]);
    setSelectedAges([]);
    setFilterFavorite(false);
    setFilterFolio(false);
  };

  const hasActiveFilters = selectedChildren.length > 0 || selectedAvatars.length > 0 || 
    selectedTopics.length > 0 || selectedAges.length > 0 || filterFavorite || filterFolio;

  // Get image for story - prefer thumbnail, fallback to first image
  const getStoryImage = (story) => {
    if (story.thumbnail) return story.thumbnail;
    if (story.images && story.images.length > 0) return story.images[0];
    return null;
  };

  return (
    <div className={`min-h-screen ${nightMode ? 'night-gradient' : 'playful-gradient'} page-enter`}>
      {isGuestMode && <GuestModeBanner />}
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="rounded-full flex-shrink-0 w-20"
            style={{
              background: nightMode ? 'var(--bg-secondary)' : 'white',
              color: nightMode ? 'var(--text-primary)' : 'inherit',
              borderColor: nightMode ? 'var(--sketch-border)' : 'inherit'
            }}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
            Story Library
          </h1>
        </div>

        {/* Filters - Compact */}
        <div className="p-2 sm:p-3 mb-4 space-y-2">
          {/* Row 1: Favorites & Clear */}
          <div className="flex flex-wrap items-center gap-1">
          Select filters
          <button
              onClick={() => setFilterFavorite(!filterFavorite)}
              className="flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-colors border"
              style={{
                background: filterFavorite ? 'var(--accent-2)' : (nightMode ? '#374151' : '#f3f4f6'),
                color: filterFavorite ? 'white' : 'var(--text-primary)',
                borderColor: filterFavorite ? 'var(--accent-2)' : (nightMode ? '#4b5563' : '#e5e7eb')
              }}
            >
              <Heart className="w-3 h-3" fill={filterFavorite ? 'white' : 'none'} />
              Fav
            </button>
            
            <button
              onClick={() => setFilterFolio(!filterFolio)}
              className="flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-colors border"
              style={{
                background: filterFolio ? 'var(--accent-4)' : (nightMode ? '#374151' : '#f3f4f6'),
                color: filterFolio ? 'white' : 'var(--text-primary)',
                borderColor: filterFolio ? 'var(--accent-4)' : (nightMode ? '#4b5563' : '#e5e7eb')
              }}
            >
              <Sparkles className="w-3 h-3" fill={filterFolio ? 'white' : 'none'} />
              Folio
            </button>
            
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-1 px-2 py-1 text-xs"
                style={{ color: 'var(--accent-2)' }}
              >
                <X className="w-3 h-3" />
                Clear
              </button>
            )}
            
            {hasActiveFilters && (
              <span className="text-xs ml-auto" style={{ color: 'var(--text-secondary)' }}>
                {filteredStories.length}/{stories.length}
              </span>
            )}
          </div>

          {/* Row 2: Child & Avatar */}
          <div className="flex flex-wrap items-center gap-1">
            {availableChildrenOptions.length > 0 && (
              <>
                <span className="text-xs font-medium mr-1" style={{ color: 'var(--text-secondary)' }}>Child:</span>
                {availableChildrenOptions.map((child) => (
                  <button
                    key={child.id}
                    onClick={() => toggleChild(child.id)}
                    className="px-2 py-0.5 rounded-full text-xs transition-colors border"
                    style={{
                      background: selectedChildren.includes(child.id) ? 'var(--accent-2)' : (nightMode ? '#374151' : '#f3f4f6'),
                      color: selectedChildren.includes(child.id) ? 'white' : 'var(--text-primary)',
                      borderColor: selectedChildren.includes(child.id) ? 'var(--accent-2)' : (nightMode ? '#4b5563' : '#e5e7eb')
                    }}
                  >
                    {child.name}
                  </button>
                ))}
              </>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {availableAvatarsOptions.length > 0 && (
              <>
                <span className="text-xs font-medium mr-1" style={{ color: 'var(--text-secondary)' }}>Avatar:</span>
                {availableAvatarsOptions.map((avatar) => (
                  <button
                    key={avatar.id}
                    onClick={() => toggleAvatar(avatar.name)}
                    className="px-2 py-0.5 rounded-full text-xs transition-colors border"
                    style={{
                      background: selectedAvatars.includes(avatar.name) ? 'var(--accent-5)' : (nightMode ? '#374151' : '#f3f4f6'),
                      color: selectedAvatars.includes(avatar.name) ? 'white' : 'var(--text-primary)',
                      borderColor: selectedAvatars.includes(avatar.name) ? 'var(--accent-5)' : (nightMode ? '#4b5563' : '#e5e7eb')
                    }}
                  >
                    {avatar.name}
                  </button>
                ))}
              </>
            )}
          </div>

          {/* Row 3: Topic */}
          <div className="flex flex-wrap items-center gap-1">
            <span className="text-xs font-medium mr-1" style={{ color: 'var(--text-secondary)' }}>Topic:</span>
            {availableTopics.length > 0 ? availableTopics.map((topic) => (
              <button
                key={topic}
                onClick={() => toggleTopic(topic)}
                className="px-2 py-0.5 rounded-full text-xs transition-colors border"
                style={{
                  background: selectedTopics.includes(topic) ? 'var(--accent-3)' : (nightMode ? '#374151' : '#f3f4f6'),
                  color: selectedTopics.includes(topic) ? 'white' : 'var(--text-primary)',
                  borderColor: selectedTopics.includes(topic) ? 'var(--accent-3)' : (nightMode ? '#4b5563' : '#e5e7eb')
                }}
              >
                {topic}
              </button>
            )) : (
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>No topics available</span>
            )}
          </div>
        </div>

        {/* Stories Grid */}
        {loading ? (
          <div className="story-card text-center py-12">
            <BookOpen className="w-16 h-16 mx-auto mb-4 animate-pulse" style={{ color: 'var(--accent-2)' }} />
            <p style={{ color: 'var(--text-secondary)' }}>Loading stories...</p>
          </div>
        ) : filteredStories.length === 0 ? (
          <div className="story-card text-center py-12">
            <BookOpen className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--accent-2)' }} />
            <p className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              {hasActiveFilters ? 'No stories match your filters' : 'No stories yet'}
            </p>
            <p style={{ color: 'var(--text-secondary)' }}>
              {hasActiveFilters ? 'Try adjusting your filters' : 'Create your first story!'}
            </p>
            {hasActiveFilters ? (
              <Button
                onClick={clearAllFilters}
                className="pill-button mt-6"
                variant="outline"
              >
                Clear Filters
              </Button>
            ) : (
              <Button
                data-testid="create-story-btn"
                onClick={() => userTier === 'free' ? navigate('/subscription') : navigate('/generate')}
                className="pill-button mt-6"
                style={{ background: 'var(--accent-2)', color: 'white' }}
              >
                {userTier === 'free' ? 'Upgrade to create a personalized story' : 'Create Story'}
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {filteredStories.map((story) => (
                <div
                  key={story.id}
                  className="story-card overflow-hidden hover:scale-102 transition-transform cursor-pointer"
                  onClick={() => navigate(`/story/${story.id}`)}
                >
                  {/* Story Thumbnail - Whole tile is clickable */}
                  <div className="aspect-video w-full overflow-hidden">
                    {getStoryImage(story) ? (
                      <img
                        src={`data:image/png;base64,${getStoryImage(story)}`}
                        alt={story.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div 
                        className="w-full h-full flex items-center justify-center"
                        style={{ background: 'var(--accent-1)' }}
                      >
                        <BookOpen className="w-12 h-12" style={{ color: 'var(--accent-2)' }} />
                      </div>
                    )}
                  </div>

                  {/* Story Info */}
                  <div className="p-4">
                    <h3 
                      className="font-bold text-lg mb-2 line-clamp-2"
                      style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}
                    >
                      {story.title}
                    </h3>
                    
                    {/* Folio Badge */}
                    {story.is_folio_story && (
                      <p className="text-xs mb-2 font-bold flex items-center gap-1" style={{ color: 'var(--accent-4)' }}>
                        <Sparkles className="w-3 h-3" /> Folio Story
                      </p>
                    )}
                    
                    {/* Topic */}
                    {story.topic && (
                      <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                        📖 {story.topic}
                      </p>
                    )}

                    {/* Story Styles */}
                    {story.story_styles && story.story_styles.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {story.story_styles.slice(0, 2).map((style, idx) => (
                          <span 
                            key={idx}
                            className="text-xs px-2 py-0.5 rounded-full capitalize"
                            style={{ 
                              background: nightMode ? 'var(--accent-3)' : 'var(--accent-1)',
                              color: nightMode ? 'white' : 'var(--accent-3)'
                            }}
                          >
                            {style}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <span 
                        className="text-xs px-2 py-1 rounded-full font-medium capitalize"
                        style={{ 
                          background: nightMode ? 'var(--accent-2)' : 'var(--accent-1)', 
                          color: nightMode ? 'white' : 'var(--accent-2)'
                        }}
                      >
                        {getLevel(story.reading_level)}
                      </span>

                      {/* Action Buttons */}
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => handleToggleFavorite(e, story.id, story.is_favorite)}
                          className="w-8 h-8"
                          title={story.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          <Heart 
                            className="w-4 h-4" 
                            fill={story.is_favorite ? 'var(--accent-2)' : 'none'}
                            style={{ color: story.is_favorite ? 'var(--accent-2)' : 'var(--text-secondary)' }}
                          />
                        </Button>
                        {!isGuestMode && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => handleDelete(e, story.id)}
                            className="w-8 h-8"
                            title="Delete story"
                          >
                            <Trash2 className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Load More */}
            {hasMore && !hasActiveFilters && (
              <div className="text-center mt-8">
                <Button
                  onClick={() => fetchStories(true)}
                  disabled={loadingMore}
                  className="pill-button"
                  style={{ background: 'var(--accent-3)', color: 'white' }}
                >
                  {loadingMore ? 'Loading...' : 'Load More Stories'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StoryLibrary;
